<script setup lang="ts">
import { MiscTrack } from '@caks/uikit/misc'
import { ChipButton } from '@caks/uikit/chip'
import { IconPlus, IconCheck } from '@caks/uikit/icon/'
import { getBanners, getFoodItemInfo } from '@caks/shared/api/user.route'
import { onMounted, ref } from 'vue'
import { useAccountStore } from '@caks/shared/store/store.account'
import { CartItem, SelectedModificator } from '@caks/shared/types/types.user'
import { FoodExtraModification, FoodItem } from '@caks/shared/types/types.api'

interface Banner {
  id: number
  media: string
  item_id: number
  item_cost: number
  has_required_modifications: boolean
}

const bannersList = ref<Banner[]>([])
const store = useAccountStore()
const router = useRouter()

const props = withDefaults(
  defineProps<{
    desktop?: boolean
  }>(),
  {
    desktop: false,
  }
)

const emit = defineEmits<{
  (e: 'openItem', id: number): void
}>()

onMounted(async () => {
  const { data } = await getBanners()

  bannersList.value = data
})

const isAddToCart = ref<null | number>(null)
const selectedBaseMods = ref<SelectedModificator[]>([])
const allFoodInfo = shallowRef<FoodItem | null>(null)
const extraIngredientsSelected = ref<FoodExtraModification[]>([])
const removedCheckboxList = ref([])
const removableList = computed(() => {
  return allFoodInfo.value?.available_modifications.removable_modifications
})
const removedList = computed(() => {
  if (removableList.value) {
    return removableList.value.filter((el) => {
      return removedCheckboxList.value.includes(el.id)
    })
  }
  return []
})

// Оптимизировать
const addToCart = async (foodId: number) => {
  let data: any

  //@ts-ignore
  const res = await getFoodItemInfo(foodId)

  if (res.status === 'ERR') throw res.description ?? res.message

  data = res.data[0]

  const baseVariants = (data as FoodItem).available_modifications.base_modifications

  //@ts-ignore
  selectedBaseMods.value = baseVariants.map((el) => {
    return { modification_id: el.id, name: el.name, variant_id: el.available_variants[0].id }
  })

  allFoodInfo.value = data as FoodItem

  allFoodInfo.value.available_modifications.ingredients.sort((a, b) => {
    if (a.required && !b.required) return -1
    if (!a.required && b.required) return 1
    return 0
  })
  if (allFoodInfo.value) {
    const cartExtraMods = extraIngredientsSelected.value.map((el) => {
      return {
        modification_id: el.id,
        name: el.name,
      }
    })
    const cartRemovedMods = removedList.value.map((el) => {
      return {
        modification_id: el.id,
        name: el.name,
      }
    })

    const cartItem: CartItem = {
      item: allFoodInfo.value,
      //@ts-ignore
      extraModificators: cartExtraMods,
      //@ts-ignore
      removedModificators: cartRemovedMods,
      //@ts-ignore
      baseModificators: selectedBaseMods.value,
      cost: allFoodInfo.value.base_cost,
    }

    store.addToCart(cartItem)
    localStorage.setItem('cart', JSON.stringify(store.cart))
  }
}

const onChipClick = async (event: MouseEvent, { item_id, has_required_modifications }: Banner) => {
  const findedItem = store.cart.find((el) => el.item.id === item_id)

  if (!has_required_modifications && findedItem !== undefined && !isAddToCart.value) {
    event.stopPropagation()
    findedItem.amount++

    onAddItemToCart(item_id)
  }

  if (!has_required_modifications && !isAddToCart.value) {
    event.stopPropagation()

    if (store.profile || props.desktop) {
      await addToCart(item_id)

      onAddItemToCart(item_id)
    } else {
      router.push('/auth/phone')
    }
  }
}

const onAddItemToCart = (item_id: number) => {
  isAddToCart.value = item_id
  setTimeout(() => {
    isAddToCart.value = null
  }, 700)
}

const onBannerClick = (item_id: number) => {
  if (props.desktop) {
    emit('openItem', item_id)
  } else {
    router.push(`/food/${item_id}`)
  }
}
</script>

<template>
  <misc-track gap="8px" height="108px" item-width="274px" :desktop="desktop">
    <div
      v-if="bannersList.length"
      v-for="banner in bannersList"
      class="banner"
      :class="{ desktop }"
      @click="onBannerClick(banner.item_id)"
      :style="`background-image: url(${banner.media})`"
    >
      <chip-button
        class="banner__button"
        @click="($event: MouseEvent) => onChipClick($event, banner)"
      >
        <transition-group name="list">
          <div v-if="isAddToCart !== banner.item_id" class="banner__button-inner">
            <p><span>от </span> {{ `${banner.item_cost}₽` }}</p>
            <icon-plus />
          </div>
          <div v-else>
            <icon-check />
          </div>
        </transition-group>
      </chip-button>
    </div>
    <skeletor v-else v-for="n of 3" class="banner" />
  </misc-track>
</template>

<style scoped lang="scss">
.banner {
  border-radius: 8px;
  position: relative;
  width: 100%;
  height: 100%;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  &__button {
    position: absolute;
    right: 22px;
    top: 50%;
    margin-top: -14px;
    width: 112px;
  }

  &__button-inner {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.desktop {
  transform: scale(0.97);
  transition: transform 0.15s ease-in-out;
  cursor: pointer;

  &:hover {
    transform: scale(1);
  }
}

.list-move,
.list-enter-active {
  transition: all 0.35s ease;
}

.list-leave-active {
  transition: all 0.35s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(-10%);
}

.list-leave-active {
  position: absolute;
  left: 20px;
  right: 20px;
  z-index: -1;
}
</style>
